import { ClassData } from '@models/class-data';
import { AccountRequirement } from '@models/events/account-requirement.enum';
import { EventSlotDataField } from '@models/events/event-slot-data-field.model';
import { EventSlotGroup } from '@models/events/event-slot-group.model';
import { TicketLayout } from '@models/tickets/ticket-layout.model';
import { DateUtils } from '@utils/date-utils';
import moment, { Moment } from 'moment-timezone';
import { EventTicket } from '@models/events/event-ticket.model';
import { EventAttendee } from '@models/event-attendance/event-attendee.model';
import { DataField } from '@models/data-fields/data-field.model';
import { EventAttendeeQuestion } from '@models/event-attendance/event-attendee-question.model';

export class EventSlot {
  id: number;

  eventId: number;
  eventTicketId?: number;
  eventTicket?: EventTicket;
  groupId?: number;
  group?: EventSlotGroup;
  layoutId?: number;
  layout?: TicketLayout;

  startDate: Date;
  endDate: Date;
  timeZone: string;
  allDay: boolean;

  capacity?: number;
  hostConfirmsEveryBooking?: boolean;
  waitlistEnabled?: boolean;

  numSoldTickets?: number;
  numLockedTickets?: number;

  accountRequirement?: AccountRequirement;
  dataFields?: EventSlotDataField[];
  attendees?: EventAttendee[];

  constructor(json: ClassData<EventSlot>) {
    this.id = +json.id;

    this.eventId = +json.eventId;
    if (json.eventTicketId) {
      this.eventTicketId = +json.eventTicketId;
    }

    if (json.eventTicket) {
      this.eventTicket = new EventTicket(json.eventTicket);
    }

    if (json.group) {
      this.groupId = json.groupId;
      this.group = new EventSlotGroup(json.group);
    }

    if (json.layout) {
      this.layoutId = json.layoutId;
      this.layout = new TicketLayout(json.layout);
    }

    this.startDate = new Date(json.startDate);
    this.endDate = new Date(json.endDate);
    this.timeZone = json.timeZone;
    this.allDay = json.allDay;

    if (json.capacity) {
      this.capacity = +json.capacity;
    }

    if (json.numSoldTickets !== undefined) {
      this.numSoldTickets = +json.numSoldTickets;
    }
    if (json.numLockedTickets !== undefined) {
      this.numLockedTickets = +json.numLockedTickets;
    }

    this.hostConfirmsEveryBooking = json.hostConfirmsEveryBooking;
    this.waitlistEnabled = json.waitlistEnabled;
    this.accountRequirement = json.accountRequirement;

    if (json.dataFields) {
      this.dataFields = json.dataFields.map(
        (item) => new EventSlotDataField(item),
      );
    }

    if (json.attendees) {
      this.attendees = json.attendees.map(
        (attendee) => new EventAttendee(attendee),
      );
    }
  }

  getNumAvailableTickets(): number {
    if (!this.capacity) {
      return 0;
    }

    let availableTickets = this.capacity;

    if (this.numSoldTickets) {
      availableTickets -= this.numSoldTickets;
    }
    if (this.numLockedTickets) {
      availableTickets -= this.numLockedTickets;
    }

    return availableTickets > 0 ? availableTickets : 0;
  }

  getTimeRangeFormatted(): string {
    if (this.allDay) {
      return 'APP.SPACES.BOOKINGS.ALL_DAY';
    }
    return (
      moment(this.startDate).format('HH:mm') +
      '-' +
      moment(this.endDate).format('HH:mm')
    );
  }

  getStartDateTzMoment(): Moment {
    return moment.tz(this.startDate, this.timeZone);
  }

  getEndDateTzMoment(): Moment {
    return moment.tz(this.endDate, this.timeZone);
  }

  getSlotDateDisplay(currentLang: string | undefined): string {
    if (!this.startDate) {
      return '';
    }
    let dateInfo =
      DateUtils.formatDateDay(this.startDate, currentLang) +
      ', ' +
      moment(this.startDate).format('DD.MM.YYYY, HH:mm');

    if (this.endDate && this.endsOnSameDate()) {
      dateInfo += `-${moment(this.endDate).format('HH:mm')}`;
    }

    return dateInfo;
  }

  endsOnSameDate(): boolean {
    if (!this.endDate) {
      return false;
    }

    return moment(this.startDate).isSame(this.endDate, 'day');
  }

  getGroupName(): string | undefined {
    if (!this.group) {
      return undefined;
    }

    return this.group.name;
  }

  getDataFields(): DataField[] | undefined {
    if (this.dataFields && this.dataFields.length > 0) {
      return this.dataFields
        .sort((a, b) => a.order - b.order)
        .map((slotDataField) => slotDataField.dataField);
    }

    return undefined;
  }

  getDataFieldsAsEventAttendeeQuestion(): EventAttendeeQuestion[] | undefined {
    return this.dataFields
      ?.sort((a, b) => a.order - b.order)
      .filter((slotDataField) => !!slotDataField.dataField)
      .map((slotDataField) => slotDataField.getAsEventAttendeeQuestion()!);
  }
}
